// src/store/authStore.js
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';

const useAuthStore = create(
  persist(
    (set, get) => ({
      // Initial State
      token: null,
      user: null,
      loading: false,
      error: null,
      isAuthenticated: false,

      // Login - Save user details and token
      login: async (email, password) => {
        set({ loading: true, error: null });
        
        try {
          const response = await api.post('/login', { email, password });
          
          const { accessToken, user } = response.data;

          if (!accessToken) {
            throw new Error('No token received from server');
          }

          // Enhance user data with defaults if missing
          const enhancedUser = {
            id: user.id || user._id || Date.now().toString(),
            email: user.email,
            name: user.name || user.fullName || email.split('@')[0],
            role: user.role || 'admin',
            avatar: user.avatar || null,
            ...user
          };

          // Save to Zustand store
          set({ 
            token: accessToken,
            user: enhancedUser,
            isAuthenticated: true,
            loading: false,
            error: null
          });

          // Save to localStorage
          localStorage.setItem('token', accessToken);
          localStorage.setItem('user', JSON.stringify(enhancedUser));
          
          return { success: true, user: enhancedUser };
          
        } catch (err) {
          set({ 
            token: null,
            user: null,
            isAuthenticated: false,
            loading: false,
            error: err.response?.data?.message || err.message || 'Login failed'
          });
          return { success: false, error: err.response?.data?.message || err.message };
        }
      },

      // Logout
      logout: async () => {
        try {
          const token = get().token;
          if (token) {
            await api.post('/logout', {}, {
              headers: { Authorization: `Bearer ${token}` }
            }).catch(() => {
              // Silent catch - server might not be available
            });
          }
        } catch (err) {
          // Silent catch
        } finally {
          // Clear all data
          set({ 
            token: null,
            user: null,
            isAuthenticated: false,
            loading: false,
            error: null
          });
          
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('auth-storage');
        }
      },

      // Update user profile
      updateUser: async (updatedData) => {
        try {
          const currentUser = get().user;
          const token = get().token;
          
          // Call API to update user
          const response = await api.put('/user/profile', updatedData, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const updatedUser = { ...currentUser, ...response.data, ...updatedData };
          
          set({ user: updatedUser });
          localStorage.setItem('user', JSON.stringify(updatedUser));
          
          return { success: true, user: updatedUser };
        } catch (err) {
          return { success: false, error: err.message };
        }
      },

      getToken: () => get().token,
      getUser: () => get().user,
      checkAuth: () => get().isAuthenticated,
      clearError: () => set({ error: null }),
      
      initialize: () => {
        const token = localStorage.getItem('token');
        const user = localStorage.getItem('user');
        
        if (token && user) {
          try {
            const parsedUser = JSON.parse(user);
            set({ 
              token,
              user: parsedUser,
              isAuthenticated: true,
              loading: false
            });
          } catch (e) {
            // Silent error - clear corrupted data
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            localStorage.removeItem('auth-storage');
          }
        }
      }
    }),
    {
      name: 'auth-storage',
      getStorage: () => localStorage,
      partialize: (state) => ({ 
        token: state.token, 
        user: state.user,
        isAuthenticated: state.isAuthenticated 
      })
    }
  )
);

export default useAuthStore;